// DatePicker.jsx
import React, { useEffect, useRef, useState } from "react";
import "./DatePicker.css";
import { TbChevronLeft, TbChevronRight, TbCalendarMonth } from "react-icons/tb";
import { useLanguage } from "../../LanguageProvider";

const DatePicker = ({
  onDateSelect,
  toggleLabel,
  style,
  buttonStyle,
  initialDate = null,
  dropdownPosition = "bottom", // "bottom" | "top"
  minDate = null,
  resetDate = null,
  dropdownAlign = "left", // "left" | "right"  <-- YENİ: varsayılan mevcut davranış (left)
}) => {
  const { language, getTranslation } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [isYearSelectionOpen, setIsYearSelectionOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(
    initialDate ? new Date(initialDate) : null
  );
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Geçici state'ler
  const [tempSelectedDate, setTempSelectedDate] = useState(null);
  const [tempSelectedDay, setTempSelectedDay] = useState(null);

  const datePickerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setIsOpen(false);
        resetTempValues();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (selectedDate && minDate) {
      const min = new Date(minDate).setHours(0, 0, 0, 0);
      if (selectedDate.getTime() < min) {
        setSelectedDate(null);
      }
    }
    // resetDate değiştiğinde tetikleniyor
  }, [resetDate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (initialDate) {
      const dateObj = new Date(initialDate);
      setSelectedDate(dateObj);
    }
  }, [initialDate]);

  const handleDateClick = (day) => {
    const selectedFullDate = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    );

    if (minDate) {
      const min = new Date(minDate).setHours(0, 0, 0, 0);
      if (selectedFullDate.getTime() < min) return;
    }

    setTempSelectedDate(selectedFullDate);
    setTempSelectedDay(day);
  };

  const handleCancel = () => {
    resetTempValues();
    setIsOpen(false);
  };

  const handleApply = () => {
    let dateToSet = tempSelectedDate;

    if (!dateToSet) {
      const today = new Date();
      if (minDate) {
        const min = new Date(minDate).setHours(0, 0, 0, 0);
        if (today.getTime() < min) return;
      }
      dateToSet = today;
    }

    setSelectedDate(dateToSet);
    onDateSelect?.(dateToSet);
    setIsOpen(false);
  };

  const resetTempValues = () => {
    setTempSelectedDate(selectedDate);
    setTempSelectedDay(selectedDate ? selectedDate.getDate() : null);
    setCurrentMonth(new Date());
  };

  const handlePrevMonth = () => {
    setCurrentMonth((prev) => {
      const newDate = new Date(prev);
      newDate.setDate(1);
      newDate.setMonth(newDate.getMonth() - 1);
      const maxDays = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0).getDate();
      newDate.setDate(Math.min(prev.getDate(), maxDays));
      return newDate;
    });
  };

  const handleNextMonth = () => {
    setCurrentMonth((prev) => {
      const newDate = new Date(prev);
      newDate.setDate(1);
      newDate.setMonth(newDate.getMonth() + 1);
      const maxDays = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0).getDate();
      newDate.setDate(Math.min(prev.getDate(), maxDays));
      return newDate;
    });
  };

  const handleYearClick = (year) => {
    setCurrentMonth(new Date(currentMonth.setFullYear(year)));
    setIsYearSelectionOpen(false);
  };

  const handleToggleYearSelection = () => {
    setIsYearSelectionOpen(!isYearSelectionOpen);
  };

  const isDateDisabled = (date) => {
    if (!minDate) return false;
    const min = new Date(minDate).setHours(0, 0, 0, 0);
    return date.getTime() < min;
  };

  const renderDays = () => {
    const totalDays = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + 1,
      0
    ).getDate();

    const firstDayIndex = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      1
    ).getDay();

    // Pazartesi başlangıçlı takvim için ayarlama
    const adjustedFirstDayIndex = firstDayIndex === 0 ? 6 : firstDayIndex - 1;

    const days = [];

    for (let i = 0; i < adjustedFirstDayIndex; i++) {
      days.push(<div key={`empty-${i}`} className="date empty"></div>);
    }

    for (let day = 1; day <= totalDays; day++) {
      const dateObj = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth(),
        day
      );
      const disabled = isDateDisabled(dateObj);

      const isToday =
        !tempSelectedDate &&
        !selectedDate &&
        dateObj.toDateString() === new Date().toDateString();

      const isSelected = tempSelectedDay === day;

      days.push(
        <div
          key={day}
          className={`date ${isSelected ? "selected" : ""} ${
            isToday ? "selected" : ""
          } ${disabled ? "disabled" : ""}`}
          onClick={() => !disabled && handleDateClick(day)}
          onDoubleClick={handleApply}
        >
          {day}
        </div>
      );
    }
    return days;
  };

  const renderWeekdays = () => {
    const weekdays = Array.from({ length: 7 }, (_, i) =>
      new Intl.DateTimeFormat(language, { weekday: "short" }).format(
        new Date(2022, 0, i + 3) // Pazartesi başlangıcı için offset
      )
    );
    return weekdays.map((day, index) => <div key={index}>{day}</div>);
  };

  const renderYears = () => {
    const startYear = currentMonth.getFullYear() - 10;
    const endYear = currentMonth.getFullYear() + 10;
    const years = [];

    for (let year = startYear; year <= endYear; year++) {
      years.push(
        <div
          key={year}
          className={`year ${
            currentMonth.getFullYear() === year ? "selected" : ""
          }`}
          onClick={() => handleYearClick(year)}
        >
          {year}
        </div>
      );
    }
    return <div className="year-grid">{years}</div>;
  };

  return (
    <div ref={datePickerRef} style={style} className="datepicker-container">
      <button
        className="date-picker-toggle-button"
        style={buttonStyle}
        onClick={() => {
          setIsOpen(!isOpen);
          resetTempValues();
        }}
      >
        {selectedDate instanceof Date && !isNaN(selectedDate) ? (
          selectedDate?.toLocaleDateString(language, {
            day: "numeric",
            month: "long",
            year: "numeric",
          })
        ) : (
          <span
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              display: "flex",
              paddingInline: "5px",
            }}
          >
            {toggleLabel} <TbCalendarMonth size={20} />
          </span>
        )}
      </button>

      {isOpen && (
        <div
          className={`datepicker-dropdown ${
            dropdownPosition === "top" ? "dropdown-top" : ""
          } ${dropdownAlign === "right" ? "align-right" : "align-left"}`}
        >
          <div className="header">
            <button onClick={handlePrevMonth}>
              <TbChevronLeft />
            </button>
            <span onClick={handleToggleYearSelection}>
              {currentMonth.toLocaleDateString(language, {
                month: "long",
                year: "numeric",
              })}
            </span>
            <button onClick={handleNextMonth}>
              <TbChevronRight />
            </button>
          </div>

          {isYearSelectionOpen ? (
            renderYears()
          ) : (
            <>
              <div className="weekday-row">{renderWeekdays()}</div>
              <div className="grid">{renderDays()}</div>
            </>
          )}

          <div className="footer">
            <button className="cancel" onClick={handleCancel}>
              {getTranslation("cancel")}
            </button>
            <button className="apply" onClick={handleApply}>
              {getTranslation("apply")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePicker;